/**
 * Property Store — all reads and writes to the top-level `properties/`
 * collection (AD-013, Module 4, Phase 1).
 *
 * Counter pattern: same as client_av / project_av / partner_av.
 * Rules: `counters/property_av` is team-writable (widened via firestore.rules
 * after this dispatch's rules deploy). `properties/{pid}` is team-read/write,
 * no delete (soft-archive only).
 *
 * Pure / impure split: `propertyNoFromSeq` is pure (in propertyModel.ts).
 * Only the counter transaction + doc writes + live listener touch Firestore.
 */
import {
  arrayUnion,
  collection,
  doc,
  getDocs,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type Unsubscribe,
} from 'firebase/firestore';
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
} from 'firebase/storage';
import { db, storage } from './firebase';
import {
  propertyNoFromSeq,
  newPlanId,
  appendPlanVersion,
  addClientRef,
  addConcept as addConceptPure,
  selectConcept as selectConceptPure,
  normalizeProperty,
  activeConcept,
  activeVersion,
  collectDeletedChildren,
  type DeletedPropertyChild,
  renameConcept as renameConceptPure,
  softDeleteConcept as softDeleteConceptPure,
  restoreConcept as restoreConceptPure,
  purgeConcept as purgeConceptPure,
  addVersion as addVersionPure,
  renameVersion as renameVersionPure,
  selectVersion as selectVersionPure,
  softDeleteVersion as softDeleteVersionPure,
  restoreVersion as restoreVersionPure,
  purgeVersion as purgeVersionPure,
  addFloorToVersion as addFloorToVersionPure,
  renameFloor as renameFloorPure,
  softDeleteFloor as softDeleteFloorPure,
  restoreFloor as restoreFloorPure,
  purgeFloor as purgeFloorPure,
  liveConcepts,
  versionFloors,
  type DesignConcept,
  type FloorPlanVersion,
  type GridSnapshot,
  type LiveProperty,
  type OccupantEntry,
  type PropertyEngagementRef,
  type PropertyFloor,
  type PropertyNorthScreenshot,
  type PropertySeed,
  type Pt2D,
} from './propertyModel';
// Registry-delete link cascade (owner bug 2026-07-03) — one-way import
// (propertyLinkCascade reads property/project docs directly; it never imports
// this store, so no cycle).
import { cascadePropertyRemoval } from './propertyLinkCascade';
import {
  isFloorPlanUploadType,
  isFloorPlanType,
  MAX_FLOORPLAN_BYTES,
  humanSize,
  isPdfFloorPlanType,
  readImageSize,
} from './floorPlan';

// ─── Counter path ─────────────────────────────────────────────────────────

const PROPERTY_COUNTER_PATH = 'property_av';

/**
 * Atomically mint the next property serial from `counters/property_av`.
 * Same pattern as client_av / project_av. Seeded at 0 (fresh collection —
 * no legacy series to continue). First mint → seq 1 → "P00001".
 */
async function nextPropertySeq(): Promise<number> {
  const ref = doc(db, 'counters', PROPERTY_COUNTER_PATH);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const cur =
      snap.exists() && typeof snap.data().seq === 'number'
        ? (snap.data().seq as number)
        : 0;
    const next = cur + 1;
    tx.set(ref, { seq: next, updatedAt: serverTimestamp() }, { merge: true });
    return next;
  });
}

/**
 * Mint a new property id (public, for callers that need the id before create).
 * Prefer `createProperty` (which mints + writes atomically).
 */
export async function mintPropertyId(): Promise<string> {
  const seq = await nextPropertySeq();
  return propertyNoFromSeq(seq);
}

// ─── Create ───────────────────────────────────────────────────────────────

/**
 * Create a new Property document. Returns the new propertyId (P#####).
 * Mints the id from the counter, writes the doc to `properties/{pid}`.
 *
 * AD-036: an optional `clientId` seeds `clientRefs[]` so a client's properties
 * are queryable even before a project exists (intake is client-scoped).
 */
export async function createProperty(
  seed: PropertySeed,
  actorUid: string,
  clientId?: string,
): Promise<string> {
  const seq = await nextPropertySeq();
  const propertyId = propertyNoFromSeq(seq);
  const now = new Date().toISOString();

  const docBody: Record<string, unknown> = {
    propertyId,
    addressComps: seed.addressComps ?? {},
    occupants: [],
    floors: [],
    engagementRefs: [],
    clientRefs: clientId ? [{ clientId, addedAt: now }] : [],
    archived: false,
    createdAt: now,
    createdBy: actorUid,
    updatedAt: now,
  };
  if (seed.address) docBody.address = seed.address;
  if (seed.projectType != null) docBody.projectType = seed.projectType;
  if (seed.northRotation != null && !Number.isNaN(seed.northRotation)) docBody.northRotation = seed.northRotation;
  if (seed.siteLocation != null) docBody.siteLocation = seed.siteLocation;

  await setDoc(doc(db, 'properties', propertyId), docBody);
  return propertyId;
}

// ─── Listen (list) ────────────────────────────────────────────────────────

/**
 * Listen to all non-archived properties, ordered by createdAt descending.
 * Returns the Unsubscribe function.
 */
export function listenProperties(
  cb: (props: LiveProperty[]) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'properties'),
    where('archived', '==', false),
    orderBy('createdAt', 'desc'),
  );
  return onSnapshot(q, (snap) => {
    // AD-038 dual-read: normalise to Concept▸Version▸Floor on read (idempotent,
    // pure — never writes back here; the backfill script does the durable write).
    // AD-038: a whole-property trashed doc is hidden from the normal list.
    const props = snap.docs
      .map((d) => normalizeProperty(d.data() as LiveProperty))
      .filter((p) => p.trashed !== true);
    cb(props);
  });
}

/**
 * Listen to ALL properties including archived (for the "show archived" toggle).
 */
export function listenAllProperties(
  cb: (props: LiveProperty[]) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'properties'),
    orderBy('createdAt', 'desc'),
  );
  return onSnapshot(q, (snap) => {
    const props = snap.docs.map((d) => normalizeProperty(d.data() as LiveProperty));
    cb(props);
  });
}

// ─── Get (one-time read) ──────────────────────────────────────────────────

/** Get a single property by id (one-time read). Returns null if not found.
 *  AD-038 — returns the normalised (dual-read) shape so callers always see the
 *  Concept▸Version▸Floor hierarchy regardless of the stored doc's age. */
export async function getProperty(
  propertyId: string,
): Promise<LiveProperty | null> {
  const snap = await getDoc(doc(db, 'properties', propertyId));
  if (!snap.exists()) return null;
  return normalizeProperty(snap.data() as LiveProperty);
}

/** AD-038 — raw, NON-normalised read (the stored doc as-is). Used by the
 *  backfill script to detect whether a durable migration write is needed. */
export async function getRawProperty(
  propertyId: string,
): Promise<LiveProperty | null> {
  const snap = await getDoc(doc(db, 'properties', propertyId));
  if (!snap.exists()) return null;
  return snap.data() as LiveProperty;
}

// ─── Update identity ──────────────────────────────────────────────────────

/**
 * Update the identity fields of a property (address, project type, north
 * bearing, site location). Partial — only provided fields are written.
 */
export async function updatePropertyIdentity(
  propertyId: string,
  patch: Partial<
    Pick<
      LiveProperty,
      | 'address'
      | 'addressComps'
      | 'siteLocation'
      | 'projectType'
      | 'northRotation'
    >
  >,
  _actorUid: string,
): Promise<void> {
  await updateDoc(doc(db, 'properties', propertyId), {
    ...patch,
    updatedAt: new Date().toISOString(),
  });
}

// ─── Floors (basic add/remove) ────────────────────────────────────────────

/**
 * Add a floor to the property's floors[] array (Phase 2: Phase-2 shape with plans:[]).
 * Caller is responsible for dedup (don't add a floorId already present).
 * Phase-1 callers that pass a minimal `{ floorId, label }` are still accepted.
 */
export async function addPropertyFloor(
  propertyId: string,
  floor: { floorId: string; label: string; areaApprox?: number },
): Promise<void> {
  // Ensure Phase-2 shape: include `plans: []` so later plan uploads find a matching floor.
  const phaseTwo: PropertyFloor = {
    floorId: floor.floorId as PropertyFloor['floorId'],
    label: floor.label,
    plans: [],
    ...(floor.areaApprox != null ? { areaApprox: floor.areaApprox } : {}),
  };
  await updateDoc(doc(db, 'properties', propertyId), {
    floors: arrayUnion(phaseTwo),
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Remove a floor from the property's floors[] array by floorId.
 * Reads the current doc first to find the exact stored shape (arrayRemove
 * requires an exact match — Phase-1 vs Phase-2 shapes differ).
 */
export async function removePropertyFloor(
  propertyId: string,
  floorId: string,
): Promise<void> {
  const prop = await getProperty(propertyId);
  if (!prop) return;
  const updated = (prop.floors ?? []).filter((f) => f.floorId !== floorId);
  await updateDoc(doc(db, 'properties', propertyId), {
    floors: updated,
    updatedAt: new Date().toISOString(),
  });
}

// ─── Floor plan uploads ───────────────────────────────────────────────────

/**
 * Storage path for a floor-plan image on a property.
 *   properties/{propertyId}/floors/{floorId}/{planId}.{ext}
 */
function propertyFloorPlanPath(
  propertyId: string,
  floorId: string,
  planId: string,
  ext: string,
): string {
  return `properties/${propertyId}/floors/${floorId}/${planId}.${ext}`;
}

/**
 * Upload a floor-plan image (or PDF — rasterised page 1) to Storage at the
 * property-scoped path and add a new FloorPlanVersion to the floor on the
 * Property doc. Previous versions are retained; the new one is set active.
 *
 * Returns the new FloorPlanVersion.
 */
export async function uploadPropertyFloorPlan(
  propertyId: string,
  floorId: string,
  file: File,
  _actorUid: string,
  stage?: string,
): Promise<FloorPlanVersion> {
  if (!isFloorPlanUploadType(file.type)) {
    throw new Error(
      `"${file.type || 'unknown type'}" is not supported — upload a JPG/PNG image or a PDF.`,
    );
  }
  if (file.size > MAX_FLOORPLAN_BYTES) {
    throw new Error(
      `That file is ${humanSize(file.size)} — the limit is ${humanSize(MAX_FLOORPLAN_BYTES)}.`,
    );
  }

  let uploadFile = file;
  let width: number;
  let height: number;

  if (isPdfFloorPlanType(file.type)) {
    // Rasterise page 1 of the PDF to JPEG (same pattern as floorPlan.ts).
    const { pdfFileToImageFile } = await import('./imageCropRotate');
    const raster = await pdfFileToImageFile(file, 1);
    uploadFile = raster.file;
    width = raster.width;
    height = raster.height;
  } else {
    const dims = await readImageSize(file);
    width = dims.width;
    height = dims.height;
  }

  const contentType = uploadFile.type || 'image/jpeg';
  const ext = contentType === 'image/png' ? 'png' : 'jpg';
  const planId = newPlanId();
  const storagePath = propertyFloorPlanPath(propertyId, floorId, planId, ext);
  const sRef = storageRef(storage, storagePath);
  await uploadBytes(sRef, uploadFile, { contentType });
  const imageUrl = await getDownloadURL(sRef);

  const version: FloorPlanVersion = {
    planId,
    imageUrl,
    storagePath,
    contentType,
    width,
    height,
    uploadedAt: new Date().toISOString(),
    isActive: true,
    ...(stage?.trim() ? { stage: stage.trim() } : {}),
  };

  // Update the floor on the property doc: mark all existing plans inactive,
  // append the new version as active. We read-modify-write the floors array.
  const prop = await getProperty(propertyId);
  if (!prop) throw new Error(`Property ${propertyId} not found.`);
  const updatedFloors = (prop.floors ?? []).map((f) => {
    if (f.floorId !== floorId) return f;
    return { ...f, plans: appendPlanVersion(f.plans, version) };
  });
  await updateDoc(doc(db, 'properties', propertyId), {
    floors: updatedFloors,
    updatedAt: new Date().toISOString(),
  });

  return version;
}

/**
 * Upload an already-baked (cropped + rotated) derived floor-plan image to
 * the same property-scoped path and set it as the new active version.
 */
export async function uploadPropertyDerivedFloorPlan(
  propertyId: string,
  floorId: string,
  file: File,
  width: number,
  height: number,
  stage?: string,
): Promise<FloorPlanVersion> {
  if (!isFloorPlanType(file.type)) {
    throw new Error('The edited image is not a recognised image type.');
  }
  if (file.size > MAX_FLOORPLAN_BYTES) {
    throw new Error(
      `The edited image is ${humanSize(file.size)} — the limit is ${humanSize(MAX_FLOORPLAN_BYTES)}.`,
    );
  }
  const contentType = file.type || 'image/jpeg';
  const ext = contentType === 'image/png' ? 'png' : 'jpg';
  const planId = newPlanId();
  const storagePath = propertyFloorPlanPath(propertyId, floorId, planId, ext);
  const sRef = storageRef(storage, storagePath);
  await uploadBytes(sRef, file, { contentType });
  const imageUrl = await getDownloadURL(sRef);

  const version: FloorPlanVersion = {
    planId,
    imageUrl,
    storagePath,
    contentType,
    width,
    height,
    uploadedAt: new Date().toISOString(),
    isActive: true,
    ...(stage?.trim() ? { stage: stage.trim() } : {}),
  };

  const prop = await getProperty(propertyId);
  if (!prop) throw new Error(`Property ${propertyId} not found.`);
  const updatedFloors = (prop.floors ?? []).map((f) => {
    if (f.floorId !== floorId) return f;
    return { ...f, plans: appendPlanVersion(f.plans, version) };
  });
  await updateDoc(doc(db, 'properties', propertyId), {
    floors: updatedFloors,
    updatedAt: new Date().toISOString(),
  });
  return version;
}

/**
 * Set a specific plan version as the active one (and deactivate all others
 * on that floor).
 */
export async function setActivePlanVersion(
  propertyId: string,
  floorId: string,
  planId: string,
): Promise<void> {
  const prop = await getProperty(propertyId);
  if (!prop) return;
  const updatedFloors = (prop.floors ?? []).map((f) => {
    if (f.floorId !== floorId) return f;
    const updated = (f.plans ?? []).map((v) => ({
      ...v,
      isActive: v.planId === planId,
    }));
    return { ...f, plans: updated };
  });
  await updateDoc(doc(db, 'properties', propertyId), {
    floors: updatedFloors,
    updatedAt: new Date().toISOString(),
  });
}

// ─── Boundary + grid ──────────────────────────────────────────────────────

/**
 * Save the traced boundary (and optionally the computed gridId) back to
 * the property floor. Overwrites `boundary`, `boundaryRaw`, and `gridId`
 * on the matching floor; all other floor fields are preserved.
 */
export async function savePropertyFloorBoundary(
  propertyId: string,
  floorId: string,
  boundary: Pt2D[],
  boundaryRaw: Pt2D[],
  gridId?: string,
): Promise<void> {
  const prop = await getProperty(propertyId);
  if (!prop) return;
  const updatedFloors = (prop.floors ?? []).map((f) => {
    if (f.floorId !== floorId) return f;
    const patch: Partial<PropertyFloor> = { boundary, boundaryRaw };
    if (gridId != null) patch.gridId = gridId;
    return { ...f, ...patch };
  });
  await updateDoc(doc(db, 'properties', propertyId), {
    floors: updatedFloors,
    updatedAt: new Date().toISOString(),
  });
}

// ─── North screenshot ─────────────────────────────────────────────────────

/**
 * Storage path for the property's north screenshot.
 *   properties/{propertyId}/north_screenshot.{ext}
 */
function propertyNorthScreenshotPath(propertyId: string, ext: string): string {
  return `properties/${propertyId}/north_screenshot.${ext}`;
}

/**
 * Upload the true-north Google-Earth screenshot to the property-scoped
 * Storage path. Returns a PropertyNorthScreenshot ref.
 */
export async function uploadPropertyNorthScreenshot(
  propertyId: string,
  file: File,
  _actorUid: string,
): Promise<PropertyNorthScreenshot> {
  if (!isFloorPlanType(file.type)) {
    throw new Error(
      `"${file.type || 'unknown type'}" is not an image — upload a JPG or PNG screenshot.`,
    );
  }
  if (file.size > MAX_FLOORPLAN_BYTES) {
    throw new Error(
      `That image is ${humanSize(file.size)} — the limit is ${humanSize(MAX_FLOORPLAN_BYTES)}.`,
    );
  }
  const contentType = file.type || 'image/jpeg';
  const ext = contentType === 'image/png' ? 'png' : 'jpg';
  const storagePath = propertyNorthScreenshotPath(propertyId, ext);
  const sRef = storageRef(storage, storagePath);
  await uploadBytes(sRef, file, { contentType });
  const downloadUrl = await getDownloadURL(sRef);
  // Read natural pixel size for the thumbnail display.
  let width: number | undefined;
  let height: number | undefined;
  try {
    const dims = await readImageSize(file);
    width = dims.width;
    height = dims.height;
  } catch {
    // size is optional — don't fail the upload
  }
  return {
    storagePath,
    downloadUrl,
    uploadedAt: new Date().toISOString(),
    ...(width != null ? { width } : {}),
    ...(height != null ? { height } : {}),
  };
}

/**
 * Upload an already-baked (cropped) derived north screenshot. Returns the ref.
 */
export async function uploadPropertyDerivedNorthScreenshot(
  propertyId: string,
  file: File,
): Promise<PropertyNorthScreenshot> {
  if (!isFloorPlanType(file.type)) {
    throw new Error('The edited screenshot is not a recognised image type.');
  }
  if (file.size > MAX_FLOORPLAN_BYTES) {
    throw new Error(
      `The edited screenshot is ${humanSize(file.size)} — the limit is ${humanSize(MAX_FLOORPLAN_BYTES)}.`,
    );
  }
  const contentType = file.type || 'image/jpeg';
  const ext = contentType === 'image/png' ? 'png' : 'jpg';
  const storagePath = propertyNorthScreenshotPath(propertyId, ext);
  const sRef = storageRef(storage, storagePath);
  await uploadBytes(sRef, file, { contentType });
  const downloadUrl = await getDownloadURL(sRef);
  return {
    storagePath,
    downloadUrl,
    uploadedAt: new Date().toISOString(),
  };
}

/**
 * Save (or clear) the north screenshot reference on the Property doc.
 * Passing null removes the screenshot field.
 */
export async function savePropertyNorthScreenshot(
  propertyId: string,
  shot: PropertyNorthScreenshot | null,
): Promise<void> {
  await updateDoc(doc(db, 'properties', propertyId), {
    northScreenshot: shot,
    updatedAt: new Date().toISOString(),
  });
}

// ─── Property-to-project linking ──────────────────────────────────────────

/**
 * Remove a specific engagement ref from a property's `engagementRefs` by
 * projectId (read-modify-write, since the Phase-2 shapes may not match
 * arrayRemove's exact-match requirement).
 */
export async function unlinkEngagement(
  propertyId: string,
  projectId: string,
): Promise<void> {
  const prop = await getProperty(propertyId);
  if (!prop) return;
  const updated = (prop.engagementRefs ?? []).filter(
    (r) => r.projectId !== projectId,
  );
  await updateDoc(doc(db, 'properties', propertyId), {
    engagementRefs: updated,
    updatedAt: new Date().toISOString(),
  });
}

// ─── Occupants ────────────────────────────────────────────────────────────

/** Add an occupant FK (contact → property). */
export async function addOccupant(
  propertyId: string,
  entry: OccupantEntry,
): Promise<void> {
  // Strip undefined optional fields — Firestore's arrayUnion throws on undefined values.
  const safeEntry: OccupantEntry = {
    contactId: entry.contactId,
    role: entry.role,
    ...(entry.relationship?.trim() ? { relationship: entry.relationship.trim() } : {}),
  };
  await updateDoc(doc(db, 'properties', propertyId), {
    occupants: arrayUnion(safeEntry),
    updatedAt: new Date().toISOString(),
  });
}

/** Remove an occupant by contactId. */
export async function removeOccupant(
  propertyId: string,
  contactId: string,
): Promise<void> {
  const prop = await getProperty(propertyId);
  if (!prop) return;
  const updated = (prop.occupants ?? []).filter(
    (o) => o.contactId !== contactId,
  );
  await updateDoc(doc(db, 'properties', propertyId), {
    occupants: updated,
    updatedAt: new Date().toISOString(),
  });
}

// ─── Engagement refs ──────────────────────────────────────────────────────

/** Link a project engagement reference to this property. */
export async function linkEngagement(
  propertyId: string,
  ref: PropertyEngagementRef,
): Promise<void> {
  await updateDoc(doc(db, 'properties', propertyId), {
    engagementRefs: arrayUnion(ref),
    updatedAt: new Date().toISOString(),
  });
}

// ─── Client refs (AD-036) ─────────────────────────────────────────────────

/**
 * AD-036 — link a client to a property (idempotent). Reads the current
 * clientRefs[], de-dupes by clientId via the pure `addClientRef`, and writes
 * back only when the ref is new (avoids a no-op write + bumped updatedAt).
 */
export async function linkPropertyToClient(
  propertyId: string,
  clientId: string,
): Promise<void> {
  const prop = await getProperty(propertyId);
  if (!prop) return;
  const next = addClientRef(prop.clientRefs, clientId);
  // addClientRef returns the SAME array reference when the client is already present.
  if (next === (prop.clientRefs ?? [])) return;
  await updateDoc(doc(db, 'properties', propertyId), {
    clientRefs: next,
    updatedAt: new Date().toISOString(),
  });
}

/**
 * AD-036 — listen to a client's properties (those whose clientRefs[] contains
 * the client). Firestore `array-contains` cannot match on a sub-field, so we
 * listen to all properties and filter in-memory by clientRefs[].clientId. The
 * registry is small (low hundreds); a client-side filter is fine and avoids a
 * denormalised index. Includes archived (the caller badges them).
 */
export function listenPropertiesForClient(
  clientId: string,
  cb: (props: LiveProperty[]) => void,
): Unsubscribe {
  const q = query(collection(db, 'properties'), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snap) => {
    const props = snap.docs
      .map((d) => normalizeProperty(d.data() as LiveProperty))
      .filter((p) => (p.clientRefs ?? []).some((r) => r.clientId === clientId));
    cb(props);
  });
}

// ─── Intake → Property bridge (AD-036) ────────────────────────────────────

export interface UpsertPropertyScope {
  clientId: string;
  /** Present when the intake is project-scoped (links engagementRefs too). */
  projectId?: string;
  /** Engagement role for the project link — primary (default) or candidate. */
  role?: 'primary' | 'candidate';
  /** "Property A" label for L1A candidate buildings. */
  engagementLabel?: string;
}

export interface UpsertPropertyResult {
  propertyId: string;
  /** true when a new P##### was minted; false when an existing one was updated. */
  created: boolean;
}

/**
 * AD-036 — the single intake→registry bridge.
 *
 * If `existingPropertyId` is given → WRITE-THROUGH: patch the linked property's
 *   identity (address/type/north/siteLocation) so the registry stays current.
 * Else → CREATE: mint a new P##### from the seed (caller has ALREADY shown the
 *   staff create-confirm — this function never prompts; the confirm is the UI's
 *   job, per the owner "never silent" ruling).
 *
 * Either way: links the client (clientRefs[]) and — when projectId is present —
 * the engagement (engagementRefs[] / projectRefs[] reverse handled by caller).
 *
 * Returns the propertyId + whether it was newly created.
 */
export async function upsertPropertyFromIntake(
  seed: PropertySeed,
  scope: UpsertPropertyScope,
  actorUid: string,
  existingPropertyId?: string,
): Promise<UpsertPropertyResult> {
  let propertyId: string;
  let created: boolean;

  if (existingPropertyId) {
    // Write-through: keep the registry identity current with the form edits.
    await updatePropertyIdentity(
      existingPropertyId,
      {
        ...(seed.address != null ? { address: seed.address } : {}),
        ...(seed.addressComps != null ? { addressComps: seed.addressComps } : {}),
        ...(seed.siteLocation != null ? { siteLocation: seed.siteLocation } : {}),
        ...(seed.projectType != null ? { projectType: seed.projectType } : {}),
        ...(seed.northRotation != null ? { northRotation: seed.northRotation } : {}),
      },
      actorUid,
    );
    propertyId = existingPropertyId;
    created = false;
  } else {
    propertyId = await createProperty(seed, actorUid, scope.clientId);
    created = true;
  }

  // Always ensure the client link (idempotent).
  await linkPropertyToClient(propertyId, scope.clientId);

  // Link the engagement when project-scoped.
  if (scope.projectId) {
    await linkEngagement(propertyId, {
      projectId: scope.projectId,
      role: scope.role ?? 'primary',
      ...(scope.engagementLabel ? { label: scope.engagementLabel } : {}),
      addedAt: new Date().toISOString(),
    });
  }

  return { propertyId, created };
}

// ─── Design Concepts (AD-036) ─────────────────────────────────────────────

/**
 * AD-036 — add a parallel design concept (Concept A / Concept B) to a property.
 * Read-modify-write on the `concepts[]` array via the pure `addConcept` helper
 * (which marks the first concept selected by default). Returns the new conceptId.
 */
export async function addDesignConcept(
  propertyId: string,
  name: string,
): Promise<string | null> {
  const prop = await getProperty(propertyId);
  if (!prop) return null;
  const before = prop.concepts ?? [];
  const next = addConceptPure(before, name);
  const added = next[next.length - 1];
  await updateDoc(doc(db, 'properties', propertyId), {
    concepts: next,
    updatedAt: new Date().toISOString(),
  });
  return added.conceptId;
}

/**
 * PURE — is a concept "empty" (no live version carries any floor)? An empty
 * Default concept is the implicit single-design placeholder the migration /
 * `addConcept` seeds; a write-through onto such a property should SELECT the new
 * concept (the building had no real drawings before). A property that already
 * has real drawings keeps its existing selection.
 */
export function conceptIsEmpty(c: DesignConcept): boolean {
  return (c.versions ?? []).every((v) => versionFloors(v).length === 0);
}

/**
 * AD-040 (2026-06-24) — WRITE-THROUGH: append a fully-built DesignConcept (from
 * an intake drawing set, see `buildConceptFromDrawings`) to a property, in ONE
 * read-modify-write. Owner-locked: each intake save writes a NEW concept.
 *
 * Selection rule: if the property has NO concept with real floors yet (only the
 * implicit empty Default, or none), the new concept becomes the selected one;
 * otherwise the prior selection is preserved (the new concept is an alternative).
 * Returns the new conceptId, or null when the property is missing.
 */
export async function writeDrawingSetThrough(
  propertyId: string,
  concept: DesignConcept,
): Promise<string | null> {
  const prop = await getProperty(propertyId); // normalised → concepts present
  if (!prop) return null;
  const existing = prop.concepts ?? [];
  const live = liveConcepts(existing);
  const allEmpty = live.length === 0 || live.every(conceptIsEmpty);
  // If every existing concept is empty, this write-through is the building's
  // first real design → select it (and clear any stale selection on the empties).
  const toAdd: DesignConcept = { ...concept, isSelected: allEmpty };
  const next = allEmpty
    ? [...existing.map((c) => ({ ...c, isSelected: false })), toAdd]
    : [...existing, toAdd];
  await updateDoc(doc(db, 'properties', propertyId), {
    concepts: next,
    updatedAt: new Date().toISOString(),
  });
  return toAdd.conceptId;
}

/**
 * AD-036 — mark exactly one concept as the client-selected option (clears the
 * flag on all others). No-op when the property or conceptId is absent.
 */
export async function selectDesignConcept(
  propertyId: string,
  conceptId: string,
): Promise<void> {
  const prop = await getProperty(propertyId);
  if (!prop) return;
  if (!(prop.concepts ?? []).some((c) => c.conceptId === conceptId)) return;
  const next = selectConceptPure(prop.concepts, conceptId);
  await updateDoc(doc(db, 'properties', propertyId), {
    concepts: next,
    updatedAt: new Date().toISOString(),
  });
}

// ─── AD-038 — Concept ▸ Version ▸ Floor CRUD (soft-delete + Undo + Trash) ──

/**
 * Internal — read (normalised), apply a pure `concepts[]` transform, write the
 * result back. The read is normalised so the FIRST edit of a legacy property
 * also persists the Concept▸Version▸Floor migration (idempotent + safe). All
 * AD-038 mutations funnel through here → one write path, no parallel logic.
 */
async function mutatePropertyConcepts(
  propertyId: string,
  fn: (concepts: NonNullable<LiveProperty['concepts']>) => NonNullable<LiveProperty['concepts']>,
): Promise<void> {
  const prop = await getProperty(propertyId); // normalised → concepts present
  if (!prop) return;
  const next = fn(prop.concepts ?? []);
  await updateDoc(doc(db, 'properties', propertyId), {
    concepts: next,
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Internal — patch ONE floor inside a concept+version (read-modify-write). When
 * that floor sits in the ACTIVE concept's ACTIVE version, the same patch is
 * mirrored onto the top-level `floors[]` so the existing report / grid-engine
 * seam (which reads top-level `floors[].gridId`) keeps resolving for the
 * single-design path. NEVER touches floors of other concepts/versions.
 */
async function patchVersionFloor(
  propertyId: string,
  conceptId: string,
  versionId: string,
  floorId: string,
  patch: (f: PropertyFloor) => PropertyFloor,
): Promise<void> {
  const prop = await getProperty(propertyId); // normalised
  if (!prop) return;
  const concepts = prop.concepts ?? [];
  const nextConcepts = concepts.map((c) => {
    if (c.conceptId !== conceptId) return c;
    return {
      ...c,
      versions: (c.versions ?? []).map((v) => {
        if (v.versionId !== versionId) return v;
        return {
          ...v,
          floors: (v.floors ?? []).map((f) => (f.floorId === floorId ? patch(f) : f)),
        };
      }),
    };
  });

  // Mirror to top-level floors[] only when this is the active concept+version.
  const ac = activeConcept({ concepts: nextConcepts });
  const av = ac ? activeVersion(ac) : null;
  const isActiveTarget = ac?.conceptId === conceptId && av?.versionId === versionId;

  const write: Record<string, unknown> = {
    concepts: nextConcepts,
    updatedAt: new Date().toISOString(),
  };
  if (isActiveTarget) {
    const topFloors = prop.floors ?? [];
    const exists = topFloors.some((f) => f.floorId === floorId);
    const patchedTop = exists
      ? topFloors.map((f) => (f.floorId === floorId ? patch(f) : f))
      : [...topFloors, patch({ floorId: floorId as PropertyFloor['floorId'], label: floorId, plans: [] })];
    write.floors = patchedTop;
  }
  await updateDoc(doc(db, 'properties', propertyId), write);
}

/**
 * Upload a floor-plan image (or PDF) to a floor INSIDE a concept+version. Mirrors
 * `uploadPropertyFloorPlan` but writes the new FloorPlanVersion into the
 * hierarchy floor (and the top-level floor when active — see patchVersionFloor).
 */
export async function uploadVersionFloorPlan(
  propertyId: string,
  conceptId: string,
  versionId: string,
  floorId: string,
  file: File,
  _actorUid: string,
  stage?: string,
): Promise<FloorPlanVersion> {
  if (!isFloorPlanUploadType(file.type)) {
    throw new Error(
      `"${file.type || 'unknown type'}" is not supported — upload a JPG/PNG image or a PDF.`,
    );
  }
  if (file.size > MAX_FLOORPLAN_BYTES) {
    throw new Error(`That file is ${humanSize(file.size)} — the limit is ${humanSize(MAX_FLOORPLAN_BYTES)}.`);
  }
  let uploadFile = file;
  let width: number;
  let height: number;
  if (isPdfFloorPlanType(file.type)) {
    const { pdfFileToImageFile } = await import('./imageCropRotate');
    const raster = await pdfFileToImageFile(file, 1);
    uploadFile = raster.file;
    width = raster.width;
    height = raster.height;
  } else {
    const dims = await readImageSize(file);
    width = dims.width;
    height = dims.height;
  }
  const contentType = uploadFile.type || 'image/jpeg';
  const ext = contentType === 'image/png' ? 'png' : 'jpg';
  const planId = newPlanId();
  const storagePath = propertyFloorPlanPath(propertyId, floorId, planId, ext);
  const sRef = storageRef(storage, storagePath);
  await uploadBytes(sRef, uploadFile, { contentType });
  const imageUrl = await getDownloadURL(sRef);
  const version: FloorPlanVersion = {
    planId, imageUrl, storagePath, contentType, width, height,
    uploadedAt: new Date().toISOString(), isActive: true,
    ...(stage?.trim() ? { stage: stage.trim() } : {}),
  };
  await patchVersionFloor(propertyId, conceptId, versionId, floorId, (f) => ({
    ...f, plans: appendPlanVersion(f.plans, version),
  }));
  return version;
}

/** Upload an already-baked (cropped/rotated) plan to a concept+version floor. */
export async function uploadVersionDerivedFloorPlan(
  propertyId: string,
  conceptId: string,
  versionId: string,
  floorId: string,
  file: File,
  width: number,
  height: number,
  stage?: string,
): Promise<FloorPlanVersion> {
  if (!isFloorPlanType(file.type)) throw new Error('The edited image is not a recognised image type.');
  if (file.size > MAX_FLOORPLAN_BYTES) {
    throw new Error(`The edited image is ${humanSize(file.size)} — the limit is ${humanSize(MAX_FLOORPLAN_BYTES)}.`);
  }
  const contentType = file.type || 'image/jpeg';
  const ext = contentType === 'image/png' ? 'png' : 'jpg';
  const planId = newPlanId();
  const storagePath = propertyFloorPlanPath(propertyId, floorId, planId, ext);
  const sRef = storageRef(storage, storagePath);
  await uploadBytes(sRef, file, { contentType });
  const imageUrl = await getDownloadURL(sRef);
  const version: FloorPlanVersion = {
    planId, imageUrl, storagePath, contentType, width, height,
    uploadedAt: new Date().toISOString(), isActive: true,
    ...(stage?.trim() ? { stage: stage.trim() } : {}),
  };
  await patchVersionFloor(propertyId, conceptId, versionId, floorId, (f) => ({
    ...f, plans: appendPlanVersion(f.plans, version),
  }));
  return version;
}

/** Set the active plan version on a concept+version floor. */
export async function setActiveVersionFloorPlan(
  propertyId: string,
  conceptId: string,
  versionId: string,
  floorId: string,
  planId: string,
): Promise<void> {
  await patchVersionFloor(propertyId, conceptId, versionId, floorId, (f) => ({
    ...f,
    plans: (f.plans ?? []).map((v) => ({ ...v, isActive: v.planId === planId })),
  }));
}

/** Save a traced boundary (+ optional gridId) onto a concept+version floor. */
export async function saveVersionFloorBoundary(
  propertyId: string,
  conceptId: string,
  versionId: string,
  floorId: string,
  boundary: Pt2D[],
  boundaryRaw: Pt2D[],
  gridId?: string,
): Promise<void> {
  await patchVersionFloor(propertyId, conceptId, versionId, floorId, (f) => ({
    ...f, boundary, boundaryRaw, ...(gridId != null ? { gridId } : {}),
  }));
}

// ── Concept-level ──
export function renamePropertyConcept(propertyId: string, conceptId: string, name: string) {
  return mutatePropertyConcepts(propertyId, (cs) => renameConceptPure(cs, conceptId, name));
}
export function softDeletePropertyConcept(propertyId: string, conceptId: string) {
  return mutatePropertyConcepts(propertyId, (cs) => softDeleteConceptPure(cs, conceptId));
}
export function restorePropertyConcept(propertyId: string, conceptId: string) {
  return mutatePropertyConcepts(propertyId, (cs) => restoreConceptPure(cs, conceptId));
}
export function purgePropertyConcept(propertyId: string, conceptId: string) {
  return mutatePropertyConcepts(propertyId, (cs) => purgeConceptPure(cs, conceptId));
}

// ── Version-level ──
export function addPropertyVersion(
  propertyId: string,
  conceptId: string,
  stage: string,
  opts: { carryFloors?: boolean } = {},
) {
  return mutatePropertyConcepts(propertyId, (cs) => addVersionPure(cs, conceptId, stage, opts));
}
export function renamePropertyVersion(propertyId: string, conceptId: string, versionId: string, stage: string) {
  return mutatePropertyConcepts(propertyId, (cs) => renameVersionPure(cs, conceptId, versionId, stage));
}
export function selectPropertyVersion(propertyId: string, conceptId: string, versionId: string) {
  return mutatePropertyConcepts(propertyId, (cs) => selectVersionPure(cs, conceptId, versionId));
}
export function softDeletePropertyVersion(propertyId: string, conceptId: string, versionId: string) {
  return mutatePropertyConcepts(propertyId, (cs) => softDeleteVersionPure(cs, conceptId, versionId));
}
export function restorePropertyVersion(propertyId: string, conceptId: string, versionId: string) {
  return mutatePropertyConcepts(propertyId, (cs) => restoreVersionPure(cs, conceptId, versionId));
}
export function purgePropertyVersion(propertyId: string, conceptId: string, versionId: string) {
  return mutatePropertyConcepts(propertyId, (cs) => purgeVersionPure(cs, conceptId, versionId));
}

// ── Floor-level (inside a concept+version) ──
export function addFloorToPropertyVersion(
  propertyId: string,
  conceptId: string,
  versionId: string,
  floor: PropertyFloor,
) {
  return mutatePropertyConcepts(propertyId, (cs) => addFloorToVersionPure(cs, conceptId, versionId, floor));
}
export function renamePropertyVersionFloor(propertyId: string, conceptId: string, versionId: string, floorId: string, label: string) {
  return mutatePropertyConcepts(propertyId, (cs) => renameFloorPure(cs, conceptId, versionId, floorId, label));
}
export function softDeletePropertyVersionFloor(propertyId: string, conceptId: string, versionId: string, floorId: string) {
  return mutatePropertyConcepts(propertyId, (cs) => softDeleteFloorPure(cs, conceptId, versionId, floorId));
}
export function restorePropertyVersionFloor(propertyId: string, conceptId: string, versionId: string, floorId: string) {
  return mutatePropertyConcepts(propertyId, (cs) => restoreFloorPure(cs, conceptId, versionId, floorId));
}
export function purgePropertyVersionFloor(propertyId: string, conceptId: string, versionId: string, floorId: string) {
  return mutatePropertyConcepts(propertyId, (cs) => purgeFloorPure(cs, conceptId, versionId, floorId));
}

// ─── AD-038 — whole-property recoverable Trash (parallel to client/project) ─

/**
 * Soft-delete (trash) or restore a WHOLE property. `trashed:true` hides it from
 * every normal list and surfaces it in the Trash view; restore clears the flag.
 * Ordinary client-SDK write (rules permit update; permanent doc purge would need
 * the admin-only CF + a rule change — deferred, see AD-038 BUILT note).
 *
 * LINK CASCADE (owner bug 2026-07-03): trashing now also fans out to every
 * referencing project (via this doc's `engagementRefs[]` back-refs) and removes
 * BOTH link lanes — the `projects.propertyRefs[]` registry link AND the intake
 * candidate stamp — so no project keeps pointing at a trashed property.
 * Best-effort (cascadePropertyRemoval never throws); restoring from Trash does
 * NOT re-link — staff re-link explicitly where still wanted.
 */
export async function trashProperty(
  propertyId: string,
  actorName: string,
  trashed = true,
): Promise<void> {
  await updateDoc(doc(db, 'properties', propertyId), {
    trashed,
    trashedAt: trashed ? new Date().toISOString() : null,
    trashedBy: trashed ? actorName : null,
    updatedAt: new Date().toISOString(),
  });
  // Cascade AFTER the flag (the trash itself must succeed first; the fan-out
  // is best-effort and reads the — still readable — trashed doc's back-refs).
  if (trashed) await cascadePropertyRemoval(propertyId);
}

/**
 * Listen to all TRASHED properties (whole-property trash, for the Trash view).
 * Filters in-memory (a small registry); includes archived.
 */
export function listenTrashedProperties(
  cb: (props: LiveProperty[]) => void,
): Unsubscribe {
  const q = query(collection(db, 'properties'), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snap) => {
    const props = snap.docs
      .map((d) => normalizeProperty(d.data() as LiveProperty))
      .filter((p) => p.trashed === true);
    cb(props);
  });
}

/**
 * AD-038 — listen to ALL property Trash items: whole-property soft-deletes AND
 * every soft-deleted nested concept/version/floor across all properties. The
 * registry is small, so we scan in-memory (one listener) and derive the rows via
 * the pure `collectDeletedChildren`. Includes archived properties.
 */
export function listenPropertyTrash(
  cb: (state: { properties: LiveProperty[]; children: DeletedPropertyChild[] }) => void,
): Unsubscribe {
  const q = query(collection(db, 'properties'), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snap) => {
    const all = snap.docs.map((d) => normalizeProperty(d.data() as LiveProperty));
    const properties = all.filter((p) => p.trashed === true);
    const children: DeletedPropertyChild[] = [];
    for (const p of all) {
      // a whole-trashed property's nested items aren't listed separately —
      // restoring the property restores everything under it.
      if (p.trashed === true) continue;
      children.push(...collectDeletedChildren(p));
    }
    children.sort((a, b) => ((a.deletedAt || '') < (b.deletedAt || '') ? 1 : -1));
    cb({ properties, children });
  });
}

/** AD-038 — restore one soft-deleted nested child (clears the flag). */
export async function restorePropertyChild(child: DeletedPropertyChild): Promise<void> {
  if (child.kind === 'concept') return restorePropertyConcept(child.propertyId, child.conceptId);
  if (child.kind === 'version') return restorePropertyVersion(child.propertyId, child.conceptId, child.versionId!);
  return restorePropertyVersionFloor(child.propertyId, child.conceptId, child.versionId!, child.floorId!);
}

/** AD-038 — permanently purge one soft-deleted nested child (hard-removes it
 *  from the property doc array — a client-SDK update, NOT a doc delete, so it is
 *  not blocked by the `allow delete: if false` rule on the properties doc). */
export async function purgePropertyChild(child: DeletedPropertyChild): Promise<void> {
  if (child.kind === 'concept') return purgePropertyConcept(child.propertyId, child.conceptId);
  if (child.kind === 'version') return purgePropertyVersion(child.propertyId, child.conceptId, child.versionId!);
  return purgePropertyVersionFloor(child.propertyId, child.conceptId, child.versionId!, child.floorId!);
}

// ─── Grid snapshots (Phase 2 — properties/{pid}/grids/{gridId}) ──────────

/**
 * Persist a `GridSnapshot` to `properties/{pid}/grids/{snapshot.gridId}`.
 *
 * If `snapshot.isActive === true`, also:
 *   1. Batch-sets all other grids for the same floor to `isActive: false`.
 *   2. Updates `floors[i].activeGridId` on the parent property doc.
 */
export async function saveGridSnapshot(
  propertyId: string,
  snapshot: GridSnapshot,
): Promise<void> {
  const gridRef = doc(db, 'properties', propertyId, 'grids', snapshot.gridId);

  // FIRESTORE-SAFE WRITE: the full-engine `engineJson` blob carries
  // `zonePolys: Pt[][]` — a nested array-of-arrays that Firestore's setDoc
  // rejects ("Nested arrays are not supported"). That throw silently killed
  // EVERY grid save at this choke-point. Nothing reads engineJson (write-only —
  // both the portal renderer and functions/gridFromBoundary.js RECOMPUTE the
  // grid from `boundary`), so drop it before persisting. `boundary` is
  // Pt2D[] = {x,y} objects (Firestore-legal) and is kept untouched.
  const persisted: GridSnapshot = { ...snapshot };
  delete persisted.engineJson;

  // FIRESTORE-SAFE WRITE (undefined): the snapshot's optional fields
  // (`engineVersion`, `boundaryHash`, `computedBy`) are `undefined` whenever the
  // engine result / actor doesn't supply them. Firestore's setDoc REJECTS
  // `undefined` field values ("Unsupported field value: undefined") — the same
  // choke-point that silently killed the nested-array case above — so no grid
  // doc was ever written and `floors[i].gridId` was never stamped. Strip the
  // undefined keys before persisting (these fields are simply omitted when absent).
  for (const k of Object.keys(persisted) as (keyof GridSnapshot)[]) {
    if (persisted[k] === undefined) delete persisted[k];
  }

  // Write the new snapshot doc.
  await setDoc(gridRef, persisted);

  if (snapshot.isActive) {
    // Deactivate all other grids for this floor (batch write).
    const gridsCol = collection(db, 'properties', propertyId, 'grids');
    const q = query(
      gridsCol,
      where('floorId', '==', snapshot.floorId),
      where('isActive', '==', true),
    );
    const snap = await getDocs(q);
    const batch = writeBatch(db);
    snap.docs.forEach((d) => {
      if (d.id !== snapshot.gridId) {
        batch.update(d.ref, { isActive: false });
      }
    });
    batch.commit().catch((e) => {
      console.warn('[saveGridSnapshot] batch deactivate failed:', e?.message);
    });

    // Update floors[i].activeGridId on the parent property doc.
    const prop = await getProperty(propertyId);
    if (prop) {
      const updatedFloors = (prop.floors ?? []).map((f) =>
        f.floorId === snapshot.floorId
          ? { ...f, gridId: snapshot.gridId }
          : f,
      );
      await updateDoc(doc(db, 'properties', propertyId), {
        floors: updatedFloors,
        updatedAt: new Date().toISOString(),
      });
    }
  }
}

/**
 * Mark an existing grid as active for a floor, deactivating all others for
 * that floor. Updates `floors[i].activeGridId` on the parent property doc.
 */
export async function setActiveGrid(
  propertyId: string,
  floorId: string,
  gridId: string,
): Promise<void> {
  // Load all grids for this floor.
  const gridsCol = collection(db, 'properties', propertyId, 'grids');
  const q = query(gridsCol, where('floorId', '==', floorId));
  const snap = await getDocs(q);

  const batch = writeBatch(db);
  snap.docs.forEach((d) => {
    batch.update(d.ref, { isActive: d.id === gridId });
  });
  await batch.commit();

  // Update the activeGridId on the floor entry.
  const prop = await getProperty(propertyId);
  if (prop) {
    const updatedFloors = (prop.floors ?? []).map((f) =>
      f.floorId === floorId ? { ...f, gridId } : f,
    );
    await updateDoc(doc(db, 'properties', propertyId), {
      floors: updatedFloors,
      updatedAt: new Date().toISOString(),
    });
  }
}

/**
 * Get the currently active `GridSnapshot` for a floor, or null when none is
 * active. Queries `properties/{pid}/grids` for `floorId == floorId && isActive == true`.
 */
export async function getActiveGrid(
  propertyId: string,
  floorId: string,
): Promise<GridSnapshot | null> {
  const gridsCol = collection(db, 'properties', propertyId, 'grids');
  const q = query(
    gridsCol,
    where('floorId', '==', floorId),
    where('isActive', '==', true),
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return snap.docs[0].data() as GridSnapshot;
}

// ─── Archive ──────────────────────────────────────────────────────────────

/** Soft-archive a property (no hard delete allowed by rules). Same link
 *  cascade as `trashProperty` (owner bug 2026-07-03): an archived property is
 *  hidden from every normal list, so projects must not keep pointing at it. */
export async function archiveProperty(propertyId: string): Promise<void> {
  await updateDoc(doc(db, 'properties', propertyId), {
    archived: true,
    updatedAt: new Date().toISOString(),
  });
  await cascadePropertyRemoval(propertyId);
}
